// =======================
// src/paginas/analise/processos/documentosAnalise.js
// =======================

import React, { useEffect, useState, useRef } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { EditorState } from "draft-js";
import { api, fetchDocumentoPdf } from "@api/api";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";

const piscarDocumento = keyframes`
  0%, 100% {
    background-color: #eef7ff;
    box-shadow: 0 0 0 0 rgba(23, 111, 178, 0.15);
  }
  50% {
    background-color: #d2e9fc;
    box-shadow: 0 0 0 5px rgba(23, 111, 178, 0.3);
  }
`;

const getStatus = (alteracaoAtiva, aprovacao) => {
  if (alteracaoAtiva) return { text: "Devolvido", color: "error" };
  if (aprovacao) return { text: "Aprovado", color: "success" };
  return { text: "Em análise", color: "warning" };
};

const DocumentViewer = ({ documentos = [], refreshParent }) => {
  const [open, setOpen] = useState(false);
  const [documentosVisualizados, setDocumentosVisualizados] = useState(
    () => new Set(),
  );
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocStatus, setSelectedDocStatus] = useState({
    alteracaoAtiva: false,
    aprovacao: false,
  });
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [openDevolver, setOpenDevolver] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [documentUrl, setDocumentUrl] = useState("");
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [documentError, setDocumentError] = useState("");
  const [anexoDevolucao, setAnexoDevolucao] = useState(null);
  const [erroAnexo, setErroAnexo] = useState("");
  const anexoInputRef = useRef(null);
  const [openHistorico, setOpenHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState("");
  const [documentoHistorico, setDocumentoHistorico] = useState(null);

  useEffect(() => {
    if (!open || !selectedDocument?.id) {
      setDocumentUrl("");
      setDocumentError("");
      setLoadingDocument(false);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = "";

    const carregarDocumento = async () => {
      setLoadingDocument(true);
      setDocumentError("");

      try {
        const blob = await fetchDocumentoPdf(
          api.analystDocumentoConteudo(selectedDocument.id),
          { signal: controller.signal },
        );
        objectUrl = URL.createObjectURL(blob);
        setDocumentUrl(objectUrl);
      } catch (error) {
        if (error.name !== "AbortError") {
          setDocumentError(error.message);
          setDocumentUrl("");
        }
      } finally {
        if (!controller.signal.aborted) setLoadingDocument(false);
      }
    };

    carregarDocumento();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, selectedDocument]);

  const handleOpen = (documento) => {
    setSelectedDocument(documento);
    setSelectedDocStatus({
      alteracaoAtiva: documento.alteracao_ativa,
      aprovacao: documento.aprovacao,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDocument(null);
    setSelectedDocStatus({ alteracaoAtiva: false, aprovacao: false });
  };

  const limparAnexo = () => {
    setAnexoDevolucao(null);
    setErroAnexo("");

    if (anexoInputRef.current) {
      anexoInputRef.current.value = "";
    }
  };

  const handleSelecionarAnexo = (event) => {
    const arquivo = event.target.files?.[0];

    if (!arquivo) {
      limparAnexo();
      return;
    }

    const extensaoPermitida = /\.(pdf|doc|docx|txt)$/i.test(arquivo.name);
    const tamanhoMaximo = 5 * 1024 * 1024;

    if (!extensaoPermitida) {
      setAnexoDevolucao(null);
      setErroAnexo("Somente arquivos PDF, DOC, DOCX e TXT são permitidos.");
      event.target.value = "";
      return;
    }

    if (arquivo.size > tamanhoMaximo) {
      setAnexoDevolucao(null);
      setErroAnexo("O arquivo deve ter no máximo 5 MB.");
      event.target.value = "";
      return;
    }

    setErroAnexo("");
    setAnexoDevolucao(arquivo);
  };

  const handleOpenHistorico = async (documento) => {
    setDocumentoHistorico(documento);
    setHistorico([]);
    setErroHistorico("");
    setLoadingHistorico(true);
    setOpenHistorico(true);

    try {
      const response = await fetch(api.analystDocumentHistory(documento.id), {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(
          errorText || "Erro ao carregar o histórico do documento",
        );
      }

      const data = await response.json();

      setHistorico(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
      setErroHistorico("Não foi possível carregar o histórico do documento.");
    } finally {
      setLoadingHistorico(false);
    }
  };

  const handleCloseHistorico = () => {
    setOpenHistorico(false);
    setHistorico([]);
    setErroHistorico("");
    setDocumentoHistorico(null);
  };

  const handleBaixarAnexo = async (registro) => {
    if (!registro?.possui_anexo) {
      return;
    }

    try {
      const response = await fetch(api.analystHistoryAttachment(registro.id), {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();

        throw new Error(errorText || "Erro ao baixar o anexo");
      }

      const arquivoBlob = await response.blob();
      const urlTemporaria = URL.createObjectURL(arquivoBlob);
      const link = document.createElement("a");

      link.href = urlTemporaria;
      link.download = registro.anexo_nome || "anexo";

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(urlTemporaria);
    } catch (error) {
      console.error("Erro ao baixar anexo:", error);

      setAlert({
        open: true,
        message: "Não foi possível baixar o anexo",
        severity: "error",
      });
    }
  };

  const handleDevolver = () => {
    setEditorState(EditorState.createEmpty());
    limparAnexo(); // ← garante uma nova devolutiva limpa
    setOpenDevolver(true);
  };

  const handleCloseDevolver = () => {
    limparAnexo(); // ← descarta arquivo selecionado ao cancelar
    setOpenDevolver(false);
  };

  const handleAprovar = async () => {
    if (!selectedDocument?.id) {
      setAlert({
        open: true,
        message: "Documento inválido",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch(api.approveDoc(selectedDocument.id), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aprovacao: true }),
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro na aprovação");
      }

      setAlert({
        open: true,
        message: "Documento aprovado com sucesso",
        severity: "success",
      });
      setOpen(false);
      refreshParent();
    } catch (error) {
      console.error("Erro ao aprovar documento:", error);
      setAlert({ open: true, message: error.message, severity: "error" });
    }
  };

  const handleSalvarDevolucao = async () => {
    if (!selectedDocument?.id) {
      setAlert({
        open: true,
        message: "Documento inválido",
        severity: "error",
      });
      return;
    }

    const observacao = editorState.getCurrentContent().getPlainText();
    if (!observacao.trim()) {
      setAlert({
        open: true,
        message: "Por favor, informe o motivo da devolução",
        severity: "warning",
      });
      return;
    }

    try {
      const formData = new FormData();

      formData.append("observacao", observacao);
      formData.append("alteracao_ativa", "true");

      if (anexoDevolucao) {
        formData.append("anexo", anexoDevolucao);
      }

      const response = await fetch(api.resentDoc(selectedDocument.id), {
        method: "PATCH",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Erro ao devolver documento");
      }

      setAlert({
        open: true,
        message: "Documento devolvido com sucesso",
        severity: "success",
      });
      limparAnexo();
      setOpenDevolver(false);
      setOpen(false);
      refreshParent();
    } catch (error) {
      console.error("Erro ao devolver documento:", error);
      setAlert({ open: true, message: error.message, severity: "error" });
    }
  };

  return (
    <Box sx={{ padding: 2, border: "1px solid #ccc", borderRadius: 2 }}>
      {alert.open && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={0.5}>
        {(documentos || []).map((doc) => {
          const { text, color } = getStatus(doc.alteracao_ativa, doc.aprovacao);
          const documentoNotificado = new URLSearchParams(
            window.location.search,
          ).get("documento");
          const destacado =
            documentoNotificado !== null &&
            String(doc.id) === documentoNotificado;

          return (
            <Grid item xs={12} key={doc.id} sx={{ order: destacado ? -1 : 0 }}>
              <Card
                sx={{
                  width: "100%",
                  boxShadow: 1,
                  border: destacado
                    ? "2px solid #176fb2"
                    : "1px solid transparent",
                  bgcolor: destacado ? "#eef7ff" : "background.paper",
                  animation:
                    destacado && !documentosVisualizados.has(String(doc.id))
                      ? `${piscarDocumento} 1.4s ease-in-out infinite`
                      : "none",
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              >
                <CardContent
                  sx={{ py: 0.5, px: 1.5, "&:last-child": { pb: 0.5 } }}
                >
                  {destacado && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: "#176fb2",
                        fontWeight: 700,
                      }}
                    >
                      Documento reenviado pela escola
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(0, 1fr) 100px auto",
                      },
                      alignItems: "center",
                      columnGap: 2,
                      rowGap: 1,
                      minHeight: "44px",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        minWidth: 0,
                        fontWeight: 500,
                        fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                        lineHeight: 1.3,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {doc.nome_documento}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={color}
                      sx={{
                        Width: { sm: "100px" },
                        whiteSpace: "nowrap",
                        fontSize: ajustarTamanhoFonteAnalista("0.8rem"),
                        textAlign: { xs: "left", sm: "center" },
                      }}
                    >
                      {text}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setDocumentosVisualizados((anteriores) => {
                            const atualizados = new Set(anteriores);
                            atualizados.add(String(doc.id));
                            return atualizados;
                          });
                          handleOpen(doc);
                        }}
                        sx={{
                          minWidth: { xs: 0, sm: "90px" },
                          flexGrow: { xs: 1, sm: 0 },
                          fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                          height: "32px",
                        }}
                      >
                        Visualizar
                      </Button>
                      {/*
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleOpenHistorico(doc)}
                        sx={{
                          minWidth: { xs: 0, sm: "85px" },
                          flexGrow: { xs: 1, sm: 0 },
                          fontSize: "0.75rem",
                          height: "32px",
                        }}
                      >
                        Histórico
                      </Button>
                      */}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xl"
        sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      >
        <DialogContent
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {selectedDocument && (
            <>
              <Box flexGrow={1} display="flex" alignItems="center">
                {loadingDocument ? (
                  <CircularProgress />
                ) : documentError ? (
                  <Alert severity="error">{documentError}</Alert>
                ) : documentUrl ? (
                  <iframe
                    title="Documento"
                    src={documentUrl}
                    width="100%"
                    height="100%"
                    style={{ border: "none" }}
                  />
                ) : null}
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDevolver}
                  disabled={selectedDocStatus.alteracaoAtiva}
                >
                  {selectedDocStatus.aprovacao ? "Correção" : "Devolver"}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleAprovar}
                  disabled={
                    selectedDocStatus.aprovacao ||
                    selectedDocStatus.alteracaoAtiva
                  }
                >
                  Aprovar
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDevolver}
        onClose={handleCloseDevolver}
        fullWidth
        maxWidth="md"
        sx={{ "& .MuiDialog-paper": { height: "80vh" } }}
      >
        <DialogContent
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Typography variant="h6" gutterBottom>
            Motivo da devolução
          </Typography>
          <Box flexGrow={1}>
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              toolbarStyle={{ fontSize: ajustarTamanhoFonteAnalista("15px") }}
              toolbar={{
                options: ["inline", "list", "textAlign"],
                inline: { options: ["bold", "italic", "underline"] },
                list: { options: ["unordered", "ordered"] },
                textAlign: { options: ["left", "center", "right", "justify"] },
              }}
              placeholder="Descreva o motivo da devolução..."
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            {/*
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
            >
              Anexar arquivo
              <input
                ref={anexoInputRef}
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleSelecionarAnexo}
              />
            </Button>
            */}

            {erroAnexo && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {erroAnexo}
              </Alert>
            )}

            {anexoDevolucao && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Typography variant="body2">
                  {anexoDevolucao.name} —{" "}
                  {(anexoDevolucao.size / 1024 / 1024).toFixed(2)} MB
                </Typography>

                <Button size="small" color="error" onClick={limparAnexo}>
                  Remover
                </Button>
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSalvarDevolucao}
            sx={{ mt: 2 }}
          >
            Salvar e Devolver
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={openHistorico}
        onClose={handleCloseHistorico}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Histórico de devoluções
          </Typography>

          {documentoHistorico && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Documento: {documentoHistorico.nome_documento}
            </Typography>
          )}

          {loadingHistorico ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : erroHistorico ? (
            <Alert severity="error">{erroHistorico}</Alert>
          ) : historico.length === 0 ? (
            <Alert severity="info">
              Nenhuma devolução registrada para este documento.
            </Alert>
          ) : (
            <Box>
              {historico.map((registro, index) => (
                <Box
                  key={registro.id}
                  sx={{
                    py: 2,
                    borderBottom:
                      index < historico.length - 1
                        ? "1px solid #e0e0e0"
                        : "none",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {registro.analista_nome || "Analista não identificado"}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {registro.data_hora
                      ? new Date(registro.data_hora).toLocaleString("pt-BR", {
                          timeZone: "America/Sao_Paulo",
                        })
                      : "Data não informada"}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mt: 1,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {registro.motivo}
                  </Typography>

                  {registro.possui_anexo && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleBaixarAnexo(registro)}
                      sx={{
                        mt: 1,
                        textTransform: "none",
                      }}
                    >
                      {registro.anexo_nome || "Baixar anexo"}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button variant="outlined" onClick={handleCloseHistorico}>
              Fechar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentViewer;
